import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { EmptyState } from "../../components/ui/EmptyState";
import { Section } from "../../components/ui/Section";
import { listMyAnnouncements, type MyAnnouncement } from "../../lib/announcements";
import { useAuth } from "../../lib/authContext";

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "";
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return "";
  const diffMs = Date.now() - created;
  const minutes = Math.max(1, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleString();
}

export default function AnnouncementsFeedScreen() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<MyAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const lastUserIdRef = useRef<string | undefined>(undefined);

  const load = useCallback(async () => {
    const uid = user?.id;
    if (typeof uid === "string" && lastUserIdRef.current !== uid) {
      hasLoadedRef.current = false;
      lastUserIdRef.current = uid;
    }
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setRows(await listMyAnnouncements());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load announcements");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [user?.id]);

  useFocusEffect(
    useCallback(() => {
      if (!authLoading && !user) {
        router.replace("/login" as Href);
        return;
      }
      if (user) void load();
    }, [authLoading, user, load])
  );

  if (authLoading || (!user && !err)) {
    return (
      <SafeAreaView className="flex-1 bg-linen" edges={["top", "left", "right"]}>
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={COLORS.FOREST.hex} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-linen" edges={["top", "left", "right"]}>
      <View className="border-b border-sand-dark px-4 py-3">
        <Button
          variant="ghost-light"
          size="sm"
          theme="light"
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/" as Href);
          }}
        >
          ← Back
        </Button>
      </View>

      {err ? (
        <Text className="px-4 pt-3 text-sm text-error" style={{ fontFamily: "DMSans_400Regular" }}>
          {err}
        </Text>
      ) : null}

      {loading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={COLORS.FOREST.hex} />
        </View>
      ) : (
        <ScrollView className="flex-1 px-4 pt-3" contentContainerStyle={{ paddingBottom: 32 }}>
          <Section title="Announcements">
            {rows.length === 0 ? (
              <EmptyState title="No announcements yet" subtitle="School-wide updates will appear here when published." />
            ) : (
              <View className="gap-2">
                {rows.map((a) => (
                  <Card
                    key={a.id}
                    variant="light"
                    padding="md"
                    onPress={() => router.push(`/announcements/${a.id}` as Href)}
                  >
                    <View className="flex-row gap-3">
                      {!a.viewed ? (
                        <View className="mt-2 h-2 w-2 shrink-0 rounded-full bg-amber" accessibilityLabel="Unread" />
                      ) : (
                        <View className="mt-2 w-2 shrink-0" />
                      )}
                      <View className="min-w-0 flex-1">
                        <Text
                          className="text-[17px] leading-snug text-text"
                          style={{ fontFamily: "CormorantGaramond_500Medium" }}
                          numberOfLines={2}
                        >
                          {a.title}
                        </Text>
                        <Text className="mt-1 text-[11px] text-text/50" style={{ fontFamily: "DMSans_400Regular" }}>
                          {formatRelativeTime(a.published_at ?? a.created_at)}
                        </Text>
                        {a.body ? (
                          <Text
                            className="mt-2 text-[13px] leading-5 text-text/65"
                            style={{ fontFamily: "DMSans_400Regular" }}
                            numberOfLines={2}
                          >
                            {a.body}
                          </Text>
                        ) : null}
                      </View>
                      <Text className="self-center text-text/35">›</Text>
                    </View>
                  </Card>
                ))}
              </View>
            )}
          </Section>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}
